import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePhotoHandler } from '../hooks/usePhotoHandler';
import '../styles/pages/HomePage.css';
import '../App.css';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const [showExamplePhoto, setShowExamplePhoto] = useState(false);
  const {
    photos,
    uploadInputRef,
    handleUploadClick,
    handleFileSelect,
    handleRemovePhoto,
    canAddMorePhotos,
    remainingPhotoSlots,
    maxPhotos,
  } = usePhotoHandler();

  const handleToggleExample = () => {
    setShowExamplePhoto(prev => !prev);
  };

  const handleSubmit = () => {
    if (photos.length === 0) {
      alert('Please add at least one photo before continuing.');
      return;
    }
    navigate('/preview', { state: { photos } });
  };

  return (
    <div className="main-content">
      {/* Step indicator */}
      <div className="upload-step-indicator">
        <div className="step-indicator">
          <div className="step active">
            <div className="step-number">1</div>
            <div className="step-label">Upload</div>
          </div>
          <div className="step-connector"></div>
          <div className="step">
            <div className="step-number">2</div>
            <div className="step-label">Preview</div>
          </div>
        </div>
      </div>

      <h1 className="main-title upload-title">Take a clear photo of the vehicle's license plate</h1>

      {photos.length > 0 ? (
        <>
          <div className="upload-description">
            <p>
              {canAddMorePhotos 
                ? `Great! You can add ${remainingPhotoSlots} more photo${remainingPhotoSlots !== 1 ? 's' : ''} or continue to preview your report.`
                : `Perfect! You've reached the maximum of ${maxPhotos} photos. Continue to preview your report.`
              }
            </p>
          </div>
          
          <div className="photo-upload-container">
            <div className="photo-preview-grid upload-mode">
              {photos.map((photo, index) => (
                <div key={photo.id} className="photo-preview-item upload-item">
                  <img src={photo.url} alt="License plate" />
                  <div className="photo-preview-overlay">
                    <div className="photo-number">{index + 1}</div>
                  </div>
                  <button 
                    className="remove-photo"
                    onClick={() => handleRemovePhoto(photo.id)}
                    aria-label="Remove photo"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                    </svg>
                  </button>
                </div>
              ))}
              {canAddMorePhotos && (
                <button className="add-photo-button" onClick={handleUploadClick}>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M10.75 4.75a.75.75 0 00-1.5 0v4.5h-4.5a.75.75 0 000 1.5h4.5v4.5a.75.75 0 001.5 0v-4.5h4.5a.75.75 0 000-1.5h-4.5v-4.5z" />
                  </svg>
                  Add More
                </button>
              )}
            </div>

            <div className="upload-photo-info">
              <p className="photo-count upload-count">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M1 8a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 018.07 3h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0016.07 6H17a2 2 0 012 2v7a2 2 0 01-2 2H3a2 2 0 01-2-2V8zm13.5 3a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zM10 14a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                </svg>
                {photos.length} of {maxPhotos} photos selected
              </p>
            </div>
          </div>

          <div className="upload-actions">
            <button className="action-btn primary-continue-btn" onClick={handleSubmit}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z" clipRule="evenodd" />
              </svg>
              Continue to Preview
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="upload-description">
            <p>Capture clear photos of the vehicle's license plate to create your report. You can upload up to {maxPhotos} photos.</p>
          </div>
          
          <div className="photo-actions">
            <button className="action-btn primary-upload-btn" onClick={handleUploadClick}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path d="M9.25 13.25a.75.75 0 001.5 0V4.636l2.955 3.129a.75.75 0 001.09-1.03l-4.25-4.5a.75.75 0 00-1.09 0l-4.25 4.5a.75.75 0 101.09 1.03L9.25 4.636v8.614z" />
                <path d="M3.5 12.75a.75.75 0 00-1.5 0v2.5A2.75 2.75 0 004.75 18h10.5A2.75 2.75 0 0018 15.25v-2.5a.75.75 0 00-1.5 0v2.5c0 .69-.56 1.25-1.25 1.25H4.75c-.69 0-1.25-.56-1.25-1.25v-2.5z" />
              </svg>
              Take / Upload Photo
            </button>
          </div>
        </>
      )}

      <button className="helper-text" onClick={handleToggleExample}>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z" clipRule="evenodd" />
        </svg>
        {showExamplePhoto ? 'Hide example' : 'See an example'}
      </button>

      {showExamplePhoto && (
        <div className="example-photo-popup">
          <img
            src="https://via.placeholder.com/320x200.png?text=Example+Plate"
            alt="License plate example"
            className="example-image"
          />
        </div>
      )}

      <p className="info-text">
        Your report will be submitted by RTM to keep your identity 100% anonymous.
      </p>

      <input
        type="file"
        accept="image/*"
        multiple
        ref={uploadInputRef}
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />
    </div>
  );
};

export default HomePage;
